import { useRef, useState } from "react"

import { 
  EmptyPlaceholder,
  ErrorPlaceholder, 
  FilterSelector, 
  LoadingPlaceholder,
  SearchInput, 
  SimpleDefinitionListItem 
} from "@components"

import { 
  BookmarkSquareIcon, 
  ChevronLeftIcon, 
  CircleStackIcon 
} from "@heroicons/react/24/outline"

import { MinusIcon, PlusIcon } from "@heroicons/react/20/solid"

import { 
  Button, 
  Chip, 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from "@heroui/react"

import { GET_GROUP_LESSON_STUDENTS_DIFF } from "@graphql"
import { useLazyQuery } from "@apollo/client"

import PropTypes from "prop-types"

const filters = [
   {
    section: "MIS Status",
    items: {
      added: { id: "added", name: "Added", dotColor: "added" },
      removed: { id: "removed", name: "Removed", dotColor: "removed" },
    }
  },
  {
    section: "Booked Status",
    items: {
      booked: { id: "booked", name: "Has Bookings", dotColor: "booking" },
      unbooked: { id: "unbooked", name: "No Bookings", dotColor: "no-booking" },
    }
  }
]

const StatusPopover = ({ content, title, trigger }) => (
  <Popover placement="bottom">
    <PopoverTrigger>
      {trigger}
    </PopoverTrigger>

    <PopoverContent className="p-4 max-w-80 bg-secondary-300">
      <h3 className="mb-2 text-base font-semibold">{title}</h3>
      <p>{content}</p>
    </PopoverContent>
  </Popover>
)

const LessonsStudents = ({ group, lessons = [] }) => {
  const [ mode, setMode ] = useState("lessons")
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedFilters, setSelectedFilters] = useState([])
  const [selectedLesson, setSelectedLesson] = useState(null)
  const [students, setStudents] = useState([])
  const searchInputRef = useRef(null)

  const [getGroupLessonStudentsDiff, { loading, error }] = useLazyQuery(GET_GROUP_LESSON_STUDENTS_DIFF, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      setStudents(data?.group?.lessons?.[0]?.groupStudentsDiff ?? [])
    }
  })

  const filterStudents = () => {
    const filteredBySearchTerm = students.filter((student) => {
      if (searchTerm === "") return true
      const studentName = `${student.forename} ${student.surname}`.toLowerCase()
      return studentName.includes(searchTerm.toLowerCase())
    })

    return filteredBySearchTerm.filter((student) => {
      if (selectedFilters.length === 0) return true
      if (selectedFilters.includes("added") && student.diff_status === "Added") return true
      if (selectedFilters.includes("removed") && student.diff_status === "Removed") return true
      if (selectedFilters.includes("booked") && student.booking_slot_count > 0) return true
      if (selectedFilters.includes("unbooked") && student.booking_slot_count === 0) return true
      return false
    })
  }
  
  const handleBackToLessons = () => {
    setMode("lessons")
    setSelectedLesson(null)
    setStudents([])
    setSearchTerm("")
    setSelectedFilters([])
  }

  const handleClearAllFilters = () => {
    clearSearchInput()
    setSelectedFilters([])
  }

  const clearSearchInput = () => {
    const clearButton = searchInputRef.current?.closest('[data-slot="base"]')?.querySelector('[data-slot="clear-button"]')
    clearButton?.click()
  }
  
  const handleToggleFilter = (selectedFilters) => {
    setSelectedFilters(selectedFilters.size > 0 ? [selectedFilters.currentKey] : [] )
  }

  const handleViewStudents = (lesson) => {
    getGroupLessonStudentsDiff({ variables: { groupId: group.id, lessonId: lesson.id } })
    setSelectedLesson(lesson)
    setMode("students")
  }

  const backToLessonsButton = (
    <Button className="mt-auto" size="lg" color="secondary" onPress={handleBackToLessons} fullWidth>
      <ChevronLeftIcon className="w-5 h-5 mr-2" />
      Back to Lessons
    </Button>
  )
  
  if (mode === "students" && loading) {
    return (
      <div className="flex flex-col h-full grow">
        <LoadingPlaceholder classNames="mb-4 grow-0" caption="Please wait. Loading students..." icon={CircleStackIcon} />
        {backToLessonsButton}
      </div>
    ) 
  }
  
  if (mode === "students" && error) {
    return (
      <div className="flex flex-col h-full grow">
        <ErrorPlaceholder classNames="mb-4 grow-0" caption="Error loading students" error={error} />
        {backToLessonsButton}
      </div>
    )
  }

  const studentStatusCounts = students.reduce((counts, student) => {
    if (student.booking_slot_count > 0) counts.booked += 1
    if (student.diff_status === "Added") counts.added += 1
    if (student.diff_status === "Removed") counts.removed += 1
    
    return counts
  }, { added: 0, removed: 0, booked: 0 })

  const filteredStudents = mode === "students" ? filterStudents() : []
  
  return (
    <div className="flex h-full">
      {mode === "lessons" && (
        <div id="lessons" className="flex flex-col grow">
          {lessons && lessons.length > 0 && (
            <div id="session-lessons" className="mb-8">
              <h2 className="mb-1 text-lg font-semibold text-content2-foreground">
                Current Session Lessons
              </h2>
              
              <p className="mb-4 text-sm text-content1-foreground">
                Click the lesson to view student details.
              </p>
  
              {lessons.map((lesson, index) => (
                <SimpleDefinitionListItem 
                  key={lesson.id} 
                  caption="Lesson" 
                  description={lesson.name} 
                  icon={<BookmarkSquareIcon />}
                  showBorder={index !== lessons.length - 1}
                  isLink={true}
                  onPress={() => handleViewStudents(lesson)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {mode === "students" && (
        <div id="students" className="flex flex-col grow">
          <h2 className="flex items-center justify-between mb-3 text-lg font-semibold text-content2-foreground">
            Students - {selectedLesson?.name} 
            <div>
              <StatusPopover 
                title="Students Added" 
                content="The number of students that are in your schedule for this lesson, but are not associated to this lesson in the MIS. If you believe this is an error, please contact your administrator to have them removed." 
                trigger={<Chip className="ml-1 font-semibold text-white bg-green-500 cursor-pointer" radius="full">{studentStatusCounts.added}</Chip>}
              />

              <StatusPopover
                title="Students Removed"
                content="The number of students that are in your MIS associated with this lesson, but are not included in your schedule for this lesson. If you believe this is an error, please contact your administrator to have them added."
                trigger={<Chip className="ml-1 font-semibold text-white bg-red-500 cursor-pointer" radius="full">{studentStatusCounts.removed}</Chip>}
              />

              <StatusPopover
                title="Number of Bookings"
                content="The number of students with bookings for this lesson."
                trigger={<Chip className="ml-1 font-semibold text-white bg-orange-400 cursor-pointer" radius="full">{studentStatusCounts.booked}</Chip>}
              />
            </div>
          </h2>

          <div className="flex items-center gap-2 mb-3">
            <SearchInput
              innerRef={searchInputRef}
              debounceMilliSeconds={300}
              placeholder="Search for a student"
              handleChange={(searchTerm) => setSearchTerm(searchTerm)}
              handleClear={() => setSearchTerm("")}
            />

            <FilterSelector
              closeOnSelect={true}
              disallowEmptySelection={false}
              filters={filters}
              selectionMode="single"
              selectedFilters={[...selectedFilters]}
              showFilterCount={true}
              toggleFilter={selectedFilters => handleToggleFilter(selectedFilters)}
              variant="solid"
            />
          </div>
          
          {filteredStudents.length === 0 && (
            <EmptyPlaceholder classNames="mt-2 grow-0" title="No Students Found" description="No students found! Try clearing any filters and searches.">
              <Button className="mt-3 font-semibold" onPress={handleClearAllFilters} color="secondary" radius="full">Clear search & filters</Button>
            </EmptyPlaceholder>
          )}
        
        
          {filteredStudents.length > 0 && (              
            <ul className="clear-right mb-4 divide-y divide-gray-300 dark:divide-content2-foreground divide-dotted">
              {filteredStudents.map((student) => (
                <li key={student.id} className="flex justify-between py-3">
                  <span className="flex items-center">
                    {student.diff_status === "Added" && <PlusIcon className="w-5 h-5 mr-2 text-green-500" />}
                    {student.diff_status === "Removed" && <MinusIcon className="w-5 h-5 mr-2 text-red-500" />}

                    <span className={student.diff_status === "Added" ? "text-green-500" : student.diff_status === "Removed" ? "text-red-500" : ""}>
                      {student.surname}, {student.forename || ""}
                    </span>
                  </span>

                  {student.booking_slot_count > 0 && (
                    <Chip classNames={{ "base": "ml-2 p-1.5", "content": "font-semibold text-white" }} color="warning" size="sm" radius="full">
                      {student.booking_slot_count}
                    </Chip>
                  )}
                </li>
              ))}
            </ul>
          )}

          {backToLessonsButton}
        </div>
      )}
    </div>
  )
}

LessonsStudents.propTypes = {
  group: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  
  lessons: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })),
}

export default LessonsStudents
